import {
  ATTACHABLE_ACCESS,
  type AttachableEntityType,
  type ModuleKey,
  type Permission,
  VARIANT_LOOKUP_PERMISSIONS,
} from '@sahab/contracts'

/** One permission, or several that are all needed. */
export type Requirement = Permission | readonly Permission[]

/**
 * What each "new document" page really needs (UX only: the API enforces the same). The create
 * permission alone opens the invoice, debit note and quotation forms. The receipt, credit note and
 * refund forms also list the customer's open invoices or credit, which the API serves under
 * `sales.read`, so they need it too. The route guards, the profile and list actions, the command
 * palette and the home shortcuts all read this one table.
 */
export const CREATE_ACCESS = {
  invoice: ['invoices.create'],
  debitNote: ['invoices.create'],
  quotation: ['quotations.create'],
  receipt: ['receipts.create', 'sales.read'],
  creditNote: ['creditNotes.create', 'sales.read'],
  refund: ['refunds.create', 'sales.read'],
} as const satisfies Record<string, readonly Permission[]>

export type CreateAction = keyof typeof CREATE_ACCESS

/** True when `granted` holds every permission `required` names. */
export function allows(
  granted: ReadonlySet<Permission> | readonly Permission[],
  required: Requirement,
): boolean {
  const has = (p: Permission) => ('has' in granted ? granted.has(p) : granted.includes(p))
  return typeof required === 'string' ? has(required) : required.every(has)
}

/**
 * Product lookup on document lines (the line editors' product picker): any one of the permissions
 * `GET /product-variants/lookup` accepts (the contract's list, which the API route declares), never
 * `products.read` alone. A clerk who may create invoices picks products without managing them.
 */
export const LOOKUP_ACCESS: readonly Permission[] = VARIANT_LOOKUP_PERMISSIONS

/** True when `granted` holds at least one of `anyOf` (the API's `@RequireAnyPermission`). */
export function allowsAny(
  granted: ReadonlySet<Permission> | readonly Permission[],
  anyOf: readonly Permission[],
): boolean {
  return anyOf.some((p) => ('has' in granted ? granted.has(p) : granted.includes(p)))
}

/**
 * Who sees a record's attachments and who adds or removes them: the contract's ATTACHABLE_ACCESS
 * (the API checks the same). No read permission: no panel at all; read only: the list without Add
 * and Remove.
 */
export function attachmentAccess(
  granted: ReadonlySet<Permission> | readonly Permission[],
  entityType: AttachableEntityType,
): { read: boolean; write: boolean } {
  const rule = ATTACHABLE_ACCESS[entityType]
  const has = (p: Permission) => ('has' in granted ? granted.has(p) : granted.includes(p))
  return { read: rule.read === null || has(rule.read), write: allowsAny(granted, rule.write) }
}

/**
 * Whether the company has a module (D15). A session without the list (older fixtures) has them all;
 * the API always sends it. UX only: the API refuses a disabled module's routes (403 MODULE_DISABLED).
 */
export function moduleEnabled(modules: readonly ModuleKey[] | undefined, module: ModuleKey): boolean {
  return modules === undefined || modules.includes(module)
}
